import { RotateCcw, BadgeCheck, Eye, MessageCircle, Sparkles } from "lucide-react";
import { LockedSection } from "./LockedSection";

const VERDICT_STYLE = {
  green: {
    color: "var(--ss-green)",
    rail: "var(--ss-green-rail)",
    tint: "var(--ss-green-bg)",
  },
  yellow: {
    color: "var(--ss-amber)",
    rail: "var(--ss-amber-rail)",
    tint: "var(--ss-amber-bg)",
  },
  red: {
    color: "var(--ss-red)",
    rail: "var(--ss-red-rail)",
    tint: "var(--ss-red-bg)",
  },
};

const FLAG_DOT = {
  bad: "var(--ss-red)",
  warn: "var(--ss-amber)",
  good: "var(--ss-green)",
};

const FLAG_LABEL = {
  bad: "High risk",
  warn: "Worth noting",
  good: "Healthy signal",
};

const REALNESS_TONE = {
  "Likely real": "var(--ss-green)",
  "Worth verifying": "var(--ss-amber)",
  "Probably scripted": "var(--ss-red)",
  "Inconclusive": "var(--ss-muted)",
};

export const VerdictCard = ({ result, onReset, onUnlock }) => {
  if (!result || !result.ok) return null;
  const v = VERDICT_STYLE[result.verdict_key];
  const realnessColor =
    REALNESS_TONE[result.realness?.assessment] || "var(--ss-muted)";
  const locked = !!result.locked;

  return (
    <section
      data-testid="verdict-card"
      data-verdict={result.verdict_key}
      className="ss-panel relative mb-12 mx-5 sm:mx-8 max-w-3xl xl:mx-auto overflow-hidden"
      style={{ borderColor: "var(--ss-rule-line)" }}
    >
      {/* Soft colored top band */}
      <div
        aria-hidden
        className="h-1.5 w-full"
        style={{ background: v.rail }}
      />

      <div className="p-6 sm:p-9">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 mb-7">
          <div className="flex-1">
            <p
              className="ss-eyebrow mb-2 flex items-center gap-2"
              style={{ color: v.color }}
              data-testid="verdict-label"
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />
              Your Read
            </p>
            <h2
              className="ss-display-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-3"
              data-testid="verdict-title"
              style={{ color: v.color }}
            >
              {result.verdict_title}
            </h2>
            <p
              className="text-base sm:text-lg italic"
              data-testid="verdict-tagline"
              style={{ color: "var(--ss-ink-2)" }}
            >
              {result.verdict_tagline}
            </p>
          </div>

          <div
            className="flex items-center gap-4 sm:flex-col sm:items-center"
            data-testid="verdict-score-block"
          >
            <div
              className="w-24 h-24 rounded-full flex flex-col items-center justify-center"
              style={{
                background: v.tint,
                border: `2px solid ${v.rail}`,
              }}
            >
              <span
                className="ss-display-tight text-3xl leading-none"
                data-testid="verdict-score"
                style={{ color: v.color }}
              >
                {result.risk_score}
              </span>
              <span
                className="ss-mono text-[9px] uppercase tracking-widest mt-1"
                style={{ color: "var(--ss-muted)" }}
              >
                / 100
              </span>
            </div>
            <p
              className="ss-eyebrow"
              data-testid="verdict-confidence"
              style={{ color: "var(--ss-muted)" }}
            >
              Conf:{" "}
              <strong style={{ color: "var(--ss-ink)" }}>
                {result.confidence}
              </strong>
            </p>
          </div>
        </div>

        <Divider />

        {locked && (
          <div
            data-testid="verdict-locked-banner"
            className="mb-7 p-4 rounded-xl flex items-start gap-3"
            style={{
              background: "var(--ss-amber-bg)",
              borderLeft: "3px solid var(--ss-amber-rail)",
            }}
          >
            <Sparkles className="w-4 h-4 mt-1 flex-shrink-0" strokeWidth={2.25} style={{ color: "var(--ss-amber)" }} />
            <div className="flex-1">
              <p className="ss-eyebrow mb-1" style={{ color: "var(--ss-amber)" }}>
                Free preview
              </p>
              <p className="text-base leading-relaxed" style={{ color: "var(--ss-ink-2)" }}>
                You’re seeing the verdict, score, and confidence. Unlock the
                Realness Audit, Subtext Read, every flag, and 3 reply options
                with a full read.
              </p>
              <button
                onClick={onUnlock}
                data-testid="verdict-unlock-cta"
                className="ss-cta mt-3"
              >
                Get the Full Read
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        {locked ? (
          <LockedSection
            title="Summary"
            testId="verdict-summary-locked"
            blurb="A plain-spoken read of the whole picture — what this person is doing and what the pattern adds up to."
            onUnlock={onUnlock}
          />
        ) : (
          <Section title="Summary" testId="verdict-summary-section">
            <p
              className="text-base sm:text-lg leading-relaxed"
              data-testid="verdict-summary"
              style={{ color: "var(--ss-ink-2)" }}
            >
              {result.summary}
            </p>
          </Section>
        )}

        {/* Realness Audit */}
        {locked ? (
          <LockedSection
            title="Realness Audit"
            testId="verdict-realness-locked"
            icon={<BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.25} />}
            blurb="Does the profile read like a real person or a copy-paste template? A direct, one-line call."
            onUnlock={onUnlock}
          />
        ) : (
          result.realness && (
            <Section
              title="Realness Audit"
              testId="verdict-realness-section"
              icon={<BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.25} />}
            >
              <div
                className="p-4 rounded-lg"
                data-testid="verdict-realness"
                style={{
                  background: "var(--ss-card-2)",
                  borderLeft: `3px solid ${realnessColor}`,
                }}
              >
                <p
                  className="ss-eyebrow mb-1.5"
                  data-testid="verdict-realness-assessment"
                  style={{ color: realnessColor }}
                >
                  {result.realness.assessment}
                </p>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: "var(--ss-ink-2)" }}
                >
                  {result.realness.note}
                </p>
              </div>
            </Section>
          )
        )}

        {/* Subtext Read */}
        {locked ? (
          <LockedSection
            title="Subtext Read"
            testId="verdict-subtext-locked"
            icon={<Eye className="w-3.5 h-3.5" strokeWidth={2.25} />}
            blurb="What they’re actually saying underneath the words. The agenda, not the surface."
            onUnlock={onUnlock}
          />
        ) : (
          result.subtext && (
            <Section
              title="Subtext Read"
              testId="verdict-subtext-section"
              icon={<Eye className="w-3.5 h-3.5" strokeWidth={2.25} />}
            >
              <p
                className="text-base sm:text-lg leading-relaxed"
                data-testid="verdict-subtext"
                style={{ color: "var(--ss-ink-2)" }}
              >
                {result.subtext}
              </p>
            </Section>
          )
        )}

        {/* Flags */}
        {locked ? (
          <LockedSection
            title="What I Noticed"
            testId="verdict-flags-locked"
            blurb="Every pattern the rule engine matched, color-coded by severity."
            onUnlock={onUnlock}
          />
        ) : (
          <Section title="What I Noticed" testId="verdict-flags-section">
            {result.flags && result.flags.length > 0 ? (
              <ul className="space-y-2.5" data-testid="verdict-flags-list">
                {result.flags.map((f, i) => (
                  <li
                    key={`${f.key}-${i}`}
                    data-testid={`verdict-flag-${f.key}`}
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{
                      background: "var(--ss-card-2)",
                      borderLeft: `3px solid ${FLAG_DOT[f.severity]}`,
                    }}
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: FLAG_DOT[f.severity] }}
                      aria-label={FLAG_LABEL[f.severity]}
                    />
                    <div className="flex-1 min-w-0">
                      <span
                        className="block text-base sm:text-[17px] font-semibold"
                        style={{ color: "var(--ss-ink)" }}
                      >
                        {f.label}
                      </span>
                    </div>
                    <span
                      className="ss-eyebrow text-[10px] shrink-0"
                      style={{ color: FLAG_DOT[f.severity] }}
                    >
                      {FLAG_LABEL[f.severity]}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: "var(--ss-muted)" }} className="italic">
                Nothing specific to flag.
              </p>
            )}
          </Section>
        )}

        {/* Why it matters */}
        {!locked && (
          <Section title="Why It Matters" testId="verdict-why-section">
            <p
              className="text-base sm:text-lg leading-relaxed"
              data-testid="verdict-why"
              style={{ color: "var(--ss-ink-2)" }}
            >
              {result.why_it_matters}
            </p>
          </Section>
        )}

        {/* What to ask next */}
        {!locked && (
          <Section
            title="What To Ask Next"
            testId="verdict-ask-section"
            icon={<MessageCircle className="w-3.5 h-3.5" strokeWidth={2.25} />}
          >
            <blockquote
              className="text-base sm:text-lg italic leading-relaxed pl-4 py-1 border-l-2"
              data-testid="verdict-ask"
              style={{ borderColor: v.rail, color: "var(--ss-ink)" }}
            >
              {result.what_to_ask_next}
            </blockquote>
          </Section>
        )}

        {/* What NOT to do */}
        {!locked && (
          <Section title="What Not To Do" testId="verdict-notdo-section">
            <p
              className="text-base sm:text-lg leading-relaxed"
              data-testid="verdict-notdo"
              style={{ color: "var(--ss-ink-2)" }}
            >
              {result.what_not_to_do}
            </p>
          </Section>
        )}

        {/* Three Reply Options */}
        {locked ? (
          <LockedSection
            title="What Do I Say Back?"
            testId="verdict-replies-locked"
            icon={<Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />}
            blurb="Three reply options — warm, direct, firm — sized to the verdict so you can paste one and move on."
            onUnlock={onUnlock}
          />
        ) : (
          result.replies && result.replies.length > 0 && (
            <Section
              title="What Do I Say Back?"
              testId="verdict-replies-section"
              icon={<Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />}
            >
              <p
                className="text-sm mb-3"
                style={{ color: "var(--ss-muted)" }}
              >
                Three options &mdash; pick the one that fits you.
              </p>
              <ul className="space-y-3" data-testid="verdict-replies-list">
                {result.replies.map((r, i) => (
                  <li
                    key={i}
                    data-testid={`verdict-reply-${i}`}
                    data-tone={r.tone}
                    className="p-4 rounded-lg"
                    style={{
                      background: "var(--ss-card-2)",
                      borderLeft: `3px solid ${v.rail}`,
                    }}
                  >
                    <p
                      className="ss-eyebrow mb-2"
                      style={{ color: v.color }}
                    >
                      Option {i + 1} &middot; {r.tone}
                    </p>
                    <p
                      className="text-base sm:text-lg leading-relaxed"
                      style={{ color: "var(--ss-ink)" }}
                    >
                      &ldquo;{r.text}&rdquo;
                    </p>
                  </li>
                ))}
              </ul>
            </Section>
          )
        )}

        <button
          data-testid="verdict-reset-btn"
          onClick={onReset}
          className="ss-ghost mt-2"
        >
          <RotateCcw className="w-4 h-4" strokeWidth={2.25} />
          Read Another
        </button>
      </div>
    </section>
  );
};

const Section = ({ title, testId, icon, children }) => (
  <div className="mb-7" data-testid={testId}>
    <h3
      className="ss-eyebrow mb-3 flex items-center gap-1.5"
      style={{ color: "var(--ss-gold)" }}
    >
      {icon}
      {title}
    </h3>
    {children}
  </div>
);

const Divider = () => (
  <div
    aria-hidden
    className="h-px mb-7"
    style={{
      background:
        "linear-gradient(to right, var(--ss-rule-line), transparent 80%)",
    }}
  />
);
